import css from './index.module.scss';
import { useEffect, useState } from 'react';
import { Drawer, IconNode, Typography } from '@components/base';
import Images from '@assets/images';
import { ENQUIRY_TYPE, OPERATION_TYPE, PORT_FIELD_TYPES } from '@helpers/constants';
import notify from '@helpers/toastify-helper';
import {
  getApiErrorMessage,
  getCountryOfOriginPayload,
  getMaterialLocationCountryPayload,
  getProductDetailsPayload
} from '@components/common/quote-wrapper/components/helper';
import { addQuote, fetchQuoteProductList, updateQuote } from '@services/quote.service';
import { fetchPortList } from '@services/util.service';
import { useParams } from 'react-router-dom';
import { useBeforeUnloadAndNavigate } from '@helpers/hooks';
import { IFilterState } from '@helpers/types/quotes';
import NonConsolidated from './components/non-consolidate';
import ConsolidatedQuote from './components/consolidate';
import DomesticNonConsolidation from './components/domestic-non-consolidation';

interface AddEditQuoteProps {
  open: boolean;
  onClose: () => void;
  isConsolidation: boolean;
  data?: any;
  isEdit?: any;
  quoteDetail: any;
  fetchEnquiryDetail?: () => void;
  isFcl: boolean;
  isMtt: boolean;
  enquiryId?: string;
  onSubmitClose: any;
  enquiryType?: string;
}

const AddEditQuote = (props: AddEditQuoteProps) => {
  const params = useParams();
  const {
    open,
    onClose,
    isConsolidation,
    data,
    isEdit,
    quoteDetail,
    fetchEnquiryDetail,
    isFcl,
    isMtt,
    onSubmitClose,
    enquiryType = ENQUIRY_TYPE.INTERNATIONAL
  } = props;

  const [quoteState, setQuoteState] = useState({
    isLoading: false,
    productOptions: [],
    portOptions: [],
    portOfLoadingOptions: [],
    portOfDischargeOptions: [],
    selectedWeightSystemChildOptions: quoteDetail.selectedWeightSystemChildOptions,
    selectedCurrency: quoteDetail.selectedCurrency,
    currency1: quoteDetail.currency1,
    primaryUnit: quoteDetail.primaryUnit,
    fxRate: quoteDetail.fxRate,
    isEdit: isEdit
  });

  const [filterState, setFilterState] = useState<IFilterState>({
    fcl: isFcl,
    mtt: isMtt,
    consolidation: isConsolidation
  });

  const { portOptions } = quoteState;

  const { fcl, mtt, consolidation } = filterState;

  useBeforeUnloadAndNavigate();

  const generateNonConsolidationReqBody = (enquiryQuote: any) => {
    const { quote } = enquiryQuote;
    const incoterm = quote?.incoTerms?.value;
    let pol = null;
    let pod = null;
    let dealIncotermLocation = null;

    if (incoterm === 'FOB') {
      pol = quote.portOfLoading?.value ?? null;
    } else if (['CIF', 'CNF', 'CFR'].includes(incoterm)) {
      pod = quote.portOfDischarge?.value ?? null;
    } else if (incoterm === 'EXW') {
      dealIncotermLocation = quote.incoTermsLocation ?? null;
    } else if (['DDP', 'DAP'].includes(incoterm)) {
      dealIncotermLocation = quote.incoTermsLocation ?? null;
    }
    const product_details = getProductDetailsPayload(quote?.productName);
    let reqBody: any = {};

    enquiryType === ENQUIRY_TYPE.DOMESTIC
      ? (reqBody = {
          product_details,
          origin: getCountryOfOriginPayload(quote?.countryOfOrigin),
          material_location_country: getMaterialLocationCountryPayload(
            quote?.materialLocationCountry
          ),
          quantity: quote?.dealQuantity,
          quantity_unit: quote?.dealQuantityUnit?.value,
          packaging: quote?.productPackaging,
          moq: quote?.moq,
          moq_unit: quote?.moqUnit?.value,
          container_size: quote?.containerSize?.value,
          weight_one_container: quote?.totalWeight,
          weight_one_container_unit: quote?.totalWeightUnit?.value,
          insurance_cost: quote?.insuranceCost,
          finance_cost: quote?.financeCost,
          profit_margin: quote?.profitMarginExpected,
          discount: quote?.discount,
          zip_code: quote.incoTermsZipCode,
          deal_incoterm_location: quote.incoTermsLocation,
          costs: {
            base_price: {
              currency: quote.base_price?.currency?.value,
              value: quote.base_price?.value,
              cost_id: isEdit === OPERATION_TYPE.EDIT ? quote.base_price?.cost_id : null,
              unit_of_weight: quote.base_price?.weightUnit?.value
            },
            final_price: {
              currency: quote?.finalQuote_selling_price?.currency.value,
              value: Math.round(quote.finalQuote_selling_price?.value * 100) / 100,
              cost_id:
                isEdit === OPERATION_TYPE.EDIT ? quote.finalQuote_selling_price?.cost_id : null,
              unit_of_weight: quote.finalQuote_selling_price?.weightUnit?.value
            },
            additional_cost: {
              currency: quote.additionalCost?.currency?.value,
              value: quote.additionalCost?.value,
              cost_id: isEdit === OPERATION_TYPE.EDIT ? quote.additionalCost?.cost_id : null
            },
            total_local_freight: {
              currency: quote.totalLocalFreight?.currency?.value,
              value: quote.totalLocalFreight?.value,
              cost_id: isEdit === OPERATION_TYPE.EDIT ? quote.totalLocalFreight?.cost_id : null
            },
            warehousing_cost: {
              currency: quote.warehousingCost?.currency?.value,
              value: quote.warehousingCost?.value,
              cost_id: isEdit === OPERATION_TYPE.EDIT ? quote.warehousingCost?.cost_id : null
            }
          }
        })
      : (reqBody = {
          product_details,
          origin: getCountryOfOriginPayload(quote?.countryOfOrigin),
          material_location_country: getMaterialLocationCountryPayload(
            quote?.materialLocationCountry
          ),
          quantity: quote?.dealQuantity,
          quantity_unit: quote?.dealQuantityUnit?.value,
          packaging: quote?.productPackaging,
          moq: quote?.moq,
          moq_unit: quote?.moqUnit?.value,
          container_size: quote?.containerSize?.value,
          weight_one_container: quote?.totalWeight,
          weight_one_container_unit: quote?.totalWeightUnit?.value,
          insurance_cost: quote?.insuranceCost,
          finance_cost: quote?.financeCost,
          profit_margin: quote?.profitMarginExpected,
          discount: quote?.discount,
          tariff: quote?.tariff,
          deal_incoterm: quote?.incoTerms?.value,
          deal_status: null,
          pol: pol,
          pod: pod,
          deal_incoterm_location: dealIncotermLocation,
          final_price_weight_unit: quote?.finalQuote_selling_price?.weightUnit?.value,
          // enquiry: params.id,
          costs: {
            base_price: {
              value: quote?.base_price?.value,
              currency: quote?.base_price?.currency?.value,
              cost_id: isEdit === OPERATION_TYPE.EDIT ? quote?.base_price?.cost_id : null,
              unit_of_weight: quote.base_price?.weightUnit?.value
            },
            ex_works_price: {
              value: quote?.final_exWorks_price?.value,
              currency: quote?.final_exWorks_price?.currency?.value ?? 'USD',
              cost_id: isEdit === OPERATION_TYPE.EDIT ? quote?.final_exWorks_price?.cost_id : null,
              unit_of_weight: quote.final_exWorks_price?.weightUnit?.value
            },
            fob_price: {
              value: quote?.fob_price?.value,
              currency: quote?.fob_price?.currency?.value,
              cost_id: isEdit === OPERATION_TYPE.EDIT ? quote?.fob_price?.cost_id : null,
              unit_of_weight: quote.fob_price?.weightUnit?.value
            },
            cif_price: {
              value: quote?.cif_price?.value,
              currency: quote?.cif_price?.currency?.value,
              cost_id: isEdit === OPERATION_TYPE.EDIT ? quote?.cif_price?.cost_id : null,
              unit_of_weight: quote.cif_price?.weightUnit?.value
            },
            cfr_price: {
              value: quote?.cfr_price?.value,
              currency: quote?.cfr_price?.currency?.value,
              cost_id: isEdit === OPERATION_TYPE.EDIT ? quote?.cfr_price?.cost_id : null,
              unit_of_weight: quote.cfr_price?.weightUnit?.value
            },
            ddp_price: {
              value: quote?.ddp_price?.value,
              currency: quote?.ddp_price?.currency?.value,
              cost_id: isEdit === OPERATION_TYPE.EDIT ? quote?.ddp_price?.cost_id : null,
              unit_of_weight: quote.ddp_price?.weightUnit?.value
            },
            final_price: {
              value: quote?.finalQuote_selling_price?.value,
              currency: quote?.finalQuote_selling_price?.currency?.value ?? 'USD',
              cost_id:
                isEdit === OPERATION_TYPE.EDIT ? quote?.finalQuote_selling_price?.cost_id : null,
              unit_of_weight: quote.finalQuote_selling_price?.weightUnit?.value
            },
            total_local_freight: {
              value: quote?.totalLocalFreight?.value,
              currency: quote?.totalLocalFreight?.currency?.value,
              cost_id: isEdit === OPERATION_TYPE.EDIT ? quote?.totalLocalFreight?.cost_id : null
            },
            total_port_and_clearance_expenses: {
              value: quote.totalPortAndClearanceExpenses?.value,
              currency: quote.totalPortAndClearanceExpenses?.currency?.value,
              cost_id:
                isEdit === OPERATION_TYPE.EDIT ? quote.totalPortAndClearanceExpenses?.cost_id : null
            },
            total_freight: {
              value: quote.totalFreight?.value,
              currency: quote.totalFreight?.currency?.value,
              cost_id: isEdit === OPERATION_TYPE.EDIT ? quote.totalFreight?.cost_id : null
            },
            additional_cost: {
              value: quote.additionalCost?.value,
              currency: quote.additionalCost?.currency?.value,
              cost_id: isEdit === OPERATION_TYPE.EDIT ? quote.additionalCost?.cost_id : null
            },
            switch_bl_cost: {
              value: quote.switchBlCost?.value,
              currency: quote.switchBlCost?.currency?.value,
              cost_id: isEdit === OPERATION_TYPE.EDIT ? quote.switchBlCost?.cost_id : null
            },
            isf_filing: {
              currency: quote.isfFiling?.currency?.value,
              value: quote.isfFiling?.value,
              cost_id: isEdit === OPERATION_TYPE.EDIT ? quote.isfFiling?.cost_id : null
            },
            transportation_cost: {
              currency: quote.drayageCharges?.currency?.value,
              value: quote.drayageCharges?.value,
              cost_id: isEdit === OPERATION_TYPE.EDIT ? quote.drayageCharges?.cost_id : null
            },
            warehousing_cost: {
              currency: quote.warehousingCost?.currency?.value,
              value: quote.warehousingCost?.value,
              cost_id: isEdit === OPERATION_TYPE.EDIT ? quote.warehousingCost?.cost_id : null
            }
          },
          custom_duty: quote.customClearance,
          zip_code: quote.incoTermsZipCode
        });

    if (isEdit == OPERATION_TYPE.EDIT) {
      reqBody.quote_id = data?.quote_id;
    }
    reqBody.product_details = getProductDetailsPayload(quote.productName);

    return reqBody;
  };

  const generateConsolidationReqBody = (enquiryQuote: any) => {
    const { quote } = enquiryQuote;
    const incoterm = quote?.incoTerms?.value;
    let pol = null;
    let pod = null;
    let dealIncotermLocation = null;

    if (incoterm === 'FOB') {
      pol = quote.portOfLoading?.value ?? null;
    } else if (['CIF', 'CNF', 'CFR'].includes(incoterm)) {
      pod = quote.portOfDischarge?.value ?? null;
    } else if (incoterm === 'EXW') {
      dealIncotermLocation = quote.incoTermsLocation ?? null;
    } else if (['DDP', 'DAP'].includes(incoterm)) {
      dealIncotermLocation = quote.incoTermsLocation ?? null;
    }
    const reqBody: any = {
      consolidation_id: data?.consolidation_id,
      products: quote.product.map((item: any) => ({
        product_details: getProductDetailsPayload(item?.productName),
        quote_id: isEdit ? item.quote_id : null,
        quantity: item.dealQuantity,
        quantity_unit: item.dealQuantityUnit?.value,
        packaging: item.productPackaging,
        costs: {
          base_price: {
            value: item.base_price?.value,
            currency: item.base_price?.currency?.value,
            cost_id: item.base_price?.cost_id
          },
          ex_works_price: {
            value: item.final_exWorks_price?.value,
            currency: item.final_exWorks_price?.currency?.value ?? 'USD',
            cost_id: item.final_exWorks_price?.cost_id
          },
          fob_price: {
            value: item.fob_price?.value,
            currency: item.fob_price?.currency?.value,
            cost_id: item.fob_price?.cost_id
          },
          cif_price: {
            value: item.cif_price?.value,
            currency: item.cif_price?.currency?.value,
            cost_id: item.cif_price?.cost_id
          },
          cfr_price: {
            value: item.cfr_price?.value,
            currency: item.cfr_price?.currency?.value,
            cost_id: item.cfr_price?.cost_id
          },
          final_price: {
            value: item.finalQuote_selling_price?.value,
            currency: item.finalQuote_selling_price?.currency?.value ?? 'USD',
            cost_id: item.finalQuote_selling_price?.cost_id
          },
          additional_cost: {
            value: item.additionalCost?.value,
            currency: item.additionalCost?.currency?.value,
            cost_id: item.additionalCost?.cost_id
          }
        },
        final_price_weight_unit: item.finalQuote_selling_price?.weightUnit?.value,
        profit_margin: item.profitMarginExpected,
        moq: item.moq,
        moq_unit: item.moqUnit?.value,
        origin: getCountryOfOriginPayload(item.countryOfOrigin),
        material_location_country: getMaterialLocationCountryPayload(item.materialLocationCountry)
      })),
      total_weight: quote.totalWeight,
      total_weight_unit: quote.totalWeightUnit?.value,
      common_costs: {
        total_local_freight: {
          value: quote.totalLocalFreight?.value,
          currency: quote.totalLocalFreight?.currency?.value,
          cost_id: quote.totalLocalFreight?.cost_id
        },
        total_port_and_clearance_expenses: {
          value: quote.totalPortAndClearanceExpenses?.value,
          currency: quote.totalPortAndClearanceExpenses?.currency?.value,
          cost_id: quote.totalPortAndClearanceExpenses?.cost_id
        },
        total_freight: {
          value: quote.totalFreight?.value,
          currency: quote.totalFreight?.currency?.value,
          cost_id: quote.totalFreight?.cost_id
        },
        switch_bl_cost: {
          value: quote.switchBlCost?.value,
          currency: quote.switchBlCost?.currency?.value,
          cost_id: quote.switchBlCost?.cost_id
        }
      },
      pol_id: pol,
      pod_id: pod,
      deal_incoterm_location: dealIncotermLocation,
      insurance_cost: quote.insuranceCost,
      finance_cost: quote.financeCost,
      discount: quote.discount,
      deal_incoterm: quote.incoTerms?.value
    };

    if (isEdit == OPERATION_TYPE.EDIT) {
      reqBody.consolidation_details_id = data?.consolidation_details_id;
    }
    return reqBody;
  };

  const onFormSubmit = async (enquiryQuote: any) => {
    const formattedData = { quote: enquiryQuote.quote[0] };
    const reqBody = consolidation
      ? generateConsolidationReqBody(formattedData)
      : generateNonConsolidationReqBody(formattedData);
    const response =
      isEdit == OPERATION_TYPE.EDIT
        ? await updateQuote(props?.enquiryId ?? params.id, reqBody)
        : await addQuote(props?.enquiryId ?? params.id, reqBody);

    if (response?.success) {
      notify({
        message: response?.data?.message ?? 'Enquiry Updated Successfully',
        severity: 'success'
      });
      onSubmitClose();
      fetchEnquiryDetail && fetchEnquiryDetail();
    } else if (response?.error) {
      notify({
        message: getApiErrorMessage(response),
        severity: 'error'
      });
    }
  };

  const filterProductOptions = async (event: any, param?: any) => {
    setQuoteState((prevState: any) => ({
      ...prevState,
      isLoading: true
    }));
    if (param) {
      const response = await fetchQuoteProductList({ product_name: param });
      if (response?.success) {
        setQuoteState((prevState: any) => ({
          ...prevState,
          productOptions: response?.data?.results ?? [],
          isLoading: false
        }));
      } else {
        notify({
          severity: 'error',
          message: response?.message
        });
        setQuoteState((prevState: any) => ({
          ...prevState,
          isLoading: false
        }));
      }
    } else {
      const response = await fetchQuoteProductList();
      if (response?.success) {
        setQuoteState((prevState: any) => ({
          ...prevState,
          productOptions: response?.data?.results ?? [],
          isLoading: false
        }));
      } else {
        notify({
          severity: 'error',
          message: response?.message
        });
        setQuoteState((prevState: any) => ({
          ...prevState,
          isLoading: false
        }));
      }
    }
  };

  const filterPortList = async () => {
    setQuoteState((prevState: any) => ({
      ...prevState,
      isLoading: true
    }));
    const data = await fetchPortList({ port_type: 'seaport' });
    if (data?.success) {
      setQuoteState((prevState: any) => ({
        ...prevState,
        portOptions: data.data.filter_options,
        portOfLoadingOptions: data.data.filter_options,
        portOfDischargeOptions: data.data.filter_options,
        isLoading: false
      }));
    } else {
      notify({
        message: data?.error ?? 'Internal Server Error',
        severity: 'error'
      });
      setQuoteState((prevState: any) => ({
        ...prevState,
        isLoading: false
      }));
    }
  };

  const filterPorts = async (event?: any, param?: any, fieldType?: string) => {
    setQuoteState((prevState: any) => ({
      ...prevState,
      isLoading: true
    }));
    if (param) {
      const portList = await fetchPortList({ search_text: param, port_type: 'seaport' });
      if (portList?.success) {
        fieldType === PORT_FIELD_TYPES.PORT_OF_LOADING
          ? setQuoteState((prevState: any) => ({
              ...prevState,
              portOfLoadingOptions: portList.data.filter_options,
              isLoading: false
            }))
          : setQuoteState((prevState: any) => ({
              ...prevState,
              portOfDischargeOptions: portList.data.filter_options,
              isLoading: false
            }));
      } else {
        setQuoteState((prevState: any) => ({
          ...prevState,
          isLoading: false
        }));
        notify({
          message: portList.error ?? 'Internal Server Error',
          severity: 'error'
        });
      }
    } else {
      fieldType === PORT_FIELD_TYPES.PORT_OF_LOADING
        ? setQuoteState((prevState: any) => ({
            ...prevState,
            portOfLoadingOptions: portOptions,
            isLoading: false
          }))
        : setQuoteState((prevState: any) => ({
            ...prevState,
            portOfDischargeOptions: portOptions,
            isLoading: false
          }));
    }
  };

  useEffect(() => {
    filterPortList();
  }, []);

  return (
    <Drawer open={open} onClose={onClose} anchor="right">
      <main className={css.modalWrapper}>
        <section className={css.modalHeader}>
          <Typography variant="h4">
            {isEdit === OPERATION_TYPE.ADD
              ? 'Add Quote'
              : isEdit === OPERATION_TYPE.EDIT
              ? 'Edit Quote'
              : isEdit === OPERATION_TYPE.DUBLICATE
              ? 'Duplicate Quote'
              : isEdit === OPERATION_TYPE.ADD_PRODUCT
              ? 'Add Product'
              : 'Add/Edit Quote'}
          </Typography>
          <IconNode
            src={Images.crossBlack}
            alt="close icon"
            component="button"
            className={css.closeButton}
            onClick={onClose}
          />
        </section>
        <section className={css.modalContent}>
          {enquiryType === ENQUIRY_TYPE.INTERNATIONAL && consolidation && (
            <ConsolidatedQuote
              quoteFormState={quoteState}
              setQuoteFormState={setQuoteState}
              filterState={filterState}
              filterProductOptions={filterProductOptions}
              filterPorts={filterPorts}
              onClose={onClose}
              onFormSubmit={onFormSubmit}
              data={data}
            />
          )}
          {enquiryType === ENQUIRY_TYPE.INTERNATIONAL && !consolidation && (
            <NonConsolidated
              quoteFormState={quoteState}
              filterState={filterState}
              filterProductOptions={filterProductOptions}
              filterPorts={filterPorts}
              onClose={onClose}
              onFormSubmit={onFormSubmit}
              data={data}
            />
          )}

          {enquiryType === ENQUIRY_TYPE.DOMESTIC && (
            <DomesticNonConsolidation
              quoteFormState={quoteState}
              filterState={filterState}
              filterProductOptions={filterProductOptions}
              onClose={onClose}
              onFormSubmit={onFormSubmit}
              data={data}
            />
          )}
        </section>
      </main>
    </Drawer>
  );
};

export default AddEditQuote;
